<template>
  <BottomSheetModal
    v-model="isOpen"
    hide-confirm
    hide-header
    hide-floating-close
    no-padding
    max-height="85vh"
    @close="handleClose"
  >
    <!-- Custom header -->
    <div class="invite-header">
      <button class="header-btn header-btn--close" @click="handleClose">
        <v-icon size="22">mdi-close</v-icon>
      </button>
      <div class="header-title">{{ title || t('invite.inviteTitle') }}</div>
      <button 
        class="header-btn header-btn--confirm" 
        :class="{ 'header-btn--disabled': !canConfirm }"
        :disabled="!canConfirm"
        @click="handleConfirm"
      >
        <v-icon size="22" color="white">mdi-check</v-icon>
      </button>
    </div>

    <div class="invite-modal">
      <slot name="top" />

      <!-- Method Selection -->
      <IosSection v-if="!searchOnly && !myPeopleOnly" form :title="t('inviteMethod')">
        <IosRow 
          :label="t('byEmail')" 
          clickable
          :show-chevron="false"
          first
          @click="inviteMethod = 'email'"
        >
          <template #value>
            <v-icon v-if="inviteMethod === 'email'" size="20" color="success">mdi-check</v-icon>
          </template>
        </IosRow>
        <IosRow 
          :label="t('invite.byPhone')"
          clickable
          :show-chevron="false"
          @click="inviteMethod = 'phone'"
        >
          <template #value>
            <v-icon v-if="inviteMethod === 'phone'" size="20" color="success">mdi-check</v-icon>
          </template>
        </IosRow>
        <IosRow 
          :label="t('searchUser')" 
          clickable
          :show-chevron="false"
          last
          @click="inviteMethod = 'search'"
        >
          <template #value>
            <v-icon v-if="inviteMethod === 'search'" size="20" color="success">mdi-check</v-icon>
          </template>
        </IosRow>
      </IosSection>

      <!-- Email input -->
      <IosSection form v-if="!searchOnly && !myPeopleOnly && inviteMethod === 'email'">
        <IosInput 
          v-model="inviteEmail"
          :placeholder="t('emailPlaceholder')"
          type="email"
          first
          last
        />
      </IosSection>

      <!-- Phone input -->
      <IosSection form v-else-if="!searchOnly && !myPeopleOnly && inviteMethod === 'phone'">
        <div class="phone-input-row">
          <div class="country-select" @click="showCountryPicker = !showCountryPicker">
            <span class="country-flag">{{ selectedCountryCode.flag }}</span>
            <span class="country-code">{{ selectedCountryCode.code }}</span>
            <v-icon size="16">mdi-chevron-down</v-icon>
          </div>
          <input 
            v-model="invitePhone"
            type="tel"
            class="phone-number-input"
            :placeholder="t('invite.phonePlaceholder')"
          />
        </div>
        
        <!-- Country picker dropdown -->
        <div v-if="showCountryPicker" class="country-picker">
          <div 
            v-for="country in countryCodes" 
            :key="country.code + country.name"
            class="country-option"
            @click="selectCountry(country)"
          >
            <span class="country-flag">{{ country.flag }}</span>
            <span class="country-name">{{ country.name }}</span>
            <span class="country-code">{{ country.code }}</span>
          </div>
        </div>
      </IosSection>

      <!-- User search -->
      <IosSection form v-else-if="searchOnly || myPeopleOnly || inviteMethod === 'search'">
        <div class="search-input-wrapper-ios">
          <v-icon size="20" class="search-icon">mdi-magnify</v-icon>
          <input 
            v-model="userSearch"
            type="text"
            class="search-input"
            :placeholder="searchPlaceholder || t('searchByName')"
            @input="onUserSearch"
          />
          <v-progress-circular v-if="searchingUsers" indeterminate size="18" width="2" class="ml-2" />
        </div>
      </IosSection>

      <!-- User search results -->
      <IosSection form v-if="(searchOnly || myPeopleOnly || inviteMethod === 'search') && userResults.length > 0">
        <IosPersonRow 
          v-for="(user, index) in userResults" 
          :key="user._id"
          :name="getUserLabel(user)"
          :avatar="user.avatar"
          :first="index === 0"
          :last="index === userResults.length - 1"
          :show-chevron="false"
          @click="selectUserForInvite(user)"
          @avatar-click="openPersonDetail(user)"
        >
          <template #actions>
            <v-btn
              icon
              size="small"
              variant="text"
              @click.stop="selectUserForInvite(user)"
            >
              <v-icon size="22" color="primary">mdi-plus</v-icon>
            </v-btn>
          </template>
        </IosPersonRow>
      </IosSection>
      
      <div v-if="(searchOnly || myPeopleOnly || inviteMethod === 'search') && !userResults.length && userSearch.length >= 2 && !searchingUsers" class="search-empty-inline">
        {{ t('noResults') }}
      </div>

      <!-- Selected users (for multiple selection mode) -->
      <IosSection v-if="multiple && selectedUsers.length > 0" form :title="t('invite.selectedTitle')">
        <IosPersonRow 
          v-for="(user, index) in selectedUsers" 
          :key="user._id"
          :name="getUserLabel(user)"
          :avatar="user.avatar"
          :first="index === 0"
          :last="index === selectedUsers.length - 1"
          :show-chevron="false"
        >
          <template #actions>
            <v-btn 
              icon 
              size="x-small" 
              variant="text"
              @click.stop="removeSelectedUser(index)"
            >
              <v-icon size="18" color="error">mdi-close</v-icon>
            </v-btn>
          </template>
        </IosPersonRow>
      </IosSection>

      <slot name="after-selected" :selected-users="selectedUsers" />

      <!-- Create dependent player button -->
      <div v-if="showCreateDependent" class="create-dependent-wrapper">
        <button class="create-dependent-btn" @click="openCreateDependent">
          <v-icon size="20" class="mr-2">mdi-account-plus-outline</v-icon>
          {{ t('createDependentPlayer') }}
        </button>
      </div>
    </div>
  </BottomSheetModal>

  <!-- User Added Notification -->
  <v-snackbar 
    v-model="showUserAddedSnackbar" 
    location="top" 
    color="transparent"
    elevation="0"
    timeout="3000"
    content-class="user-added-snackbar-content"
  >
    <div class="user-added-notification">
      <div class="user-added-avatar" :class="`ring-${getRatingColor(addedUser?.rating || 0)}`">
        <img v-if="addedUser?.avatar" :src="addedUser.avatar" class="user-added-avatar-img" />
        <span v-else class="user-added-initials">{{ getInitials(addedUser) }}</span>
      </div>
      <div class="user-added-info">
        <div class="user-added-name">{{ getUserLabel(addedUser!) }}</div>
        <div class="user-added-subtitle">
          <span class="user-added-rating">{{ addedUser?.rating || '?' }}</span>
          <span class="user-added-status">{{ $t('invite.added') }}</span>
        </div>
      </div>
      <v-icon color="success" size="24">mdi-check-circle</v-icon>
    </div>
  </v-snackbar>

  <!-- Invite Sent Notification -->
  <v-snackbar 
    v-model="showInviteSentSnackbar" 
    location="top" 
    color="transparent"
    elevation="0"
    timeout="3000"
    content-class="invite-sent-snackbar-content"
  >
    <div class="invite-sent-notification">
      <v-icon color="primary" size="28">mdi-email-fast-outline</v-icon>
      <div class="invite-sent-info">
        <div class="invite-sent-title">{{ $t('invite.invitationSent') }}</div>
        <div class="invite-sent-contact">{{ inviteSentContact }}</div>
      </div>
    </div>
  </v-snackbar>

  <!-- Error Notification -->
  <v-snackbar 
    v-model="showSnackbar" 
    location="top" 
    :color="snackbarColor" 
    timeout="2500"
  >
    {{ snackbarMessage }}
  </v-snackbar>

  <PlayerCardModal ref="playerCardRef" />

  <!-- Register Dependent Modal -->
  <RegisterDependentModal
    v-model="showDependentModal"
    :prefill-team-id="entityId"
    :prefill-position="prefillPosition"
    :prefill-role="props.role === 'goalkeepers' ? 'goalkeepers' : 'players'"
    :prefill-first-name="dependentFirstName"
    :prefill-last-name="dependentLastName"
    @saved="handleDependentSaved"
  />

  <!-- Nickname Dialog for New User -->
  <CenteredModal v-model="showNicknameDialog" :show-close-button="true" max-width="350px">
    <div class="nickname-dialog">
      <div class="nickname-dialog-icon">
        <v-icon size="48" color="primary">mdi-account-plus</v-icon>
      </div>
      <h3 class="nickname-dialog-title">{{ $t('invite.newUser') }}</h3>
      <p class="nickname-dialog-subtitle" v-html="t('invite.userNotExists', { contact: pendingContact })"></p>
      <IosInput 
        v-model="pendingNickname" 
        :placeholder="t('invite.nicknamePlaceholder')" 
        class="nickname-input"
        @keyup.enter="confirmNicknameAndInvite"
      />
      <div class="nickname-dialog-actions">
        <button class="nickname-btn nickname-btn--cancel" @click="cancelNicknameDialog">
          {{ $t('cancel') }}
        </button>
        <button 
          class="nickname-btn nickname-btn--confirm" 
          :disabled="!pendingNickname.trim()"
          @click="confirmNicknameAndInvite"
        >
          {{ $t('invite.createAndSend') }}
        </button>
      </div>
    </div>
  </CenteredModal>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import axiosInstance from '@/services/axiosInstance';
import { getUserAvatarUrl } from '@/utils/imageUtils';
import { getRatingColor } from '@/utils/ratingColor';
import BottomSheetModal from '@/components/BottomSheetModal.vue';
import CenteredModal from '@/components/CenteredModal.vue';
import PlayerCardModal from '@/components/PlayerCardModal.vue';
import RegisterDependentModal from '@/components/RegisterDependentModal.vue';
import { IosSection, IosRow, IosInput, IosPersonRow } from '@/components/ios';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';

const { t } = useI18n({ useScope: 'global' });

interface UserLite {
  _id: string;
  firstName?: string;
  lastName?: string;
  name?: string;
  nickName?: string;
  email?: string;
  avatar?: string;
  searchName?: string;
  rating?: number;
}

type CountryCode = {
  code: string;
  name: string;
  flag: string;
  label: string;
};

const props = withDefaults(defineProps<{
  modelValue: boolean;
  title?: string;
  confirmButtonLabel?: string;
  multiple?: boolean;
  alreadySelected?: UserLite[];
  searchOnly?: boolean;
  showCreateDependent?: boolean;
  searchPlaceholder?: string;
  // For sending invitations when user doesn't exist
  entityId?: string;
  entityType?: 'team' | 'league';
  role?: string;
  /** When true, restricts search to current user's dependants/referents only and forces searchOnly UI. */
  myPeopleOnly?: boolean;
}>(), {
  title: undefined,
  confirmButtonLabel: undefined,
  multiple: false,
  alreadySelected: () => [],
  searchOnly: false,
  showCreateDependent: true,
  searchPlaceholder: undefined,
  entityId: undefined,
  entityType: undefined,
  role: 'players',
  myPeopleOnly: false
});

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void;
  (e: 'done', users: UserLite[]): void;
  (e: 'userSelected', user: UserLite): void;
  (e: 'inviteSent', invite: { email?: string; phone?: string; entityType: string; role: string }): void;
}>();

// State
const isOpen = computed({
  get: () => props.modelValue,
  set: (val) => emit('update:modelValue', val)
});

const inviteMethod = ref<'email' | 'phone' | 'search'>('search');
const inviteEmail = ref('');
const invitePhone = ref('');
const inviteSubmitting = ref(false);

// Snackbar notifications
const showSnackbar = ref(false);
const snackbarMessage = ref('');
const snackbarColor = ref('success');

// Rich user notification
const showUserAddedSnackbar = ref(false);
const addedUser = ref<UserLite | null>(null);

// Invite sent notification
const showInviteSentSnackbar = ref(false);
const inviteSentContact = ref('');

// Nickname dialog for new users
const showNicknameDialog = ref(false);
const pendingNickname = ref('');
const pendingContact = ref('');
const pendingContactType = ref<'email' | 'phone'>('email');

// Dependent modal
const showDependentModal = ref(false);

const prefillPosition = computed(() => {
  if (props.role === 'goalkeepers') return 'goalie';
  return 'player';
});

const dependentFirstName = ref('');
const dependentLastName = ref('');

const openCreateDependent = () => {
  const parts = userSearch.value.trim().split(/\s+/);
  const capitalize = (s: string) => s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : '';
  if (parts.length === 1) {
    dependentFirstName.value = '';
    dependentLastName.value = capitalize(parts[0] || '');
  } else {
    dependentFirstName.value = capitalize(parts[0] || '');
    dependentLastName.value = parts.slice(1).map(capitalize).join(' ');
  }
  showDependentModal.value = true;
};

const handleDependentSaved = (data: { _id: string; firstName: string; lastName: string; name: string; rating?: number }) => {
  showDependentModal.value = false;
  if (data._id) {
    const user: UserLite = {
      _id: data._id,
      firstName: data.firstName,
      lastName: data.lastName,
      name: data.name,
      rating: data.rating,
    };
    userSearch.value = '';
    if (props.multiple) {
      if (!selectedUsers.value.some(u => u._id === data._id)) {
        selectedUsers.value.push(user);
      }
    } else {
      emit('done', [user]);
      emit('userSelected', user);
      handleClose();
    }
  }
};

const showNotification = (message: string, color: string = 'success') => {
  snackbarMessage.value = message;
  snackbarColor.value = color;
  showSnackbar.value = true;
};

const getInitials = (user: UserLite | null): string => {
  if (!user) return '';
  const first = user.firstName?.[0] || '';
  const last = user.lastName?.[0] || '';
  return (first + last).toUpperCase();
};

// User search
const userSearch = ref('');
const userResults = ref<UserLite[]>([]);
const searchingUsers = ref(false);
let userSearchTimeout: ReturnType<typeof setTimeout> | null = null;

// My dependants/referents (always loaded once, used to enrich search results)
const myDependants = ref<any[]>([]);
const dependantsLoaded = ref(false);
const fetchMyDependants = async () => {
  if (dependantsLoaded.value) return;
  try {
    const { data } = await axiosInstance.get(`${API_BASE_URL}/users/dependants`, { params: { limit: 100 } });
    myDependants.value = Array.isArray(data?.children) ? data.children : [];
  } catch {
    myDependants.value = [];
  } finally {
    dependantsLoaded.value = true;
  }
};

// Multiple selection
const selectedUsers = ref<UserLite[]>([]);

// Player profile modal
const playerCardRef = ref<InstanceType<typeof PlayerCardModal>>();

// Country codes
const countryCodes = computed<CountryCode[]>(() => [
  { code: '+420', name: t('countries.cz'), flag: '🇨🇿', label: '🇨🇿 +420' },
  { code: '+421', name: t('countries.sk'), flag: '🇸🇰', label: '🇸🇰 +421' },
  { code: '+48', name: t('countries.pl'), flag: '🇵🇱', label: '🇵🇱 +48' },
  { code: '+49', name: t('countries.de'), flag: '🇩🇪', label: '🇩🇪 +49' },
  { code: '+43', name: t('countries.at'), flag: '🇦🇹', label: '🇦🇹 +43' },
  { code: '+41', name: t('countries.ch'), flag: '🇨🇭', label: '🇨🇭 +41' },
  { code: '+36', name: t('countries.hu'), flag: '🇭🇺', label: '🇭🇺 +36' },
  { code: '+44', name: t('countries.gb'), flag: '🇬🇧', label: '🇬🇧 +44' },
  { code: '+33', name: t('countries.fr'), flag: '🇫🇷', label: '🇫🇷 +33' },
  { code: '+39', name: t('countries.it'), flag: '🇮🇹', label: '🇮🇹 +39' },
  { code: '+34', name: t('countries.es'), flag: '🇪🇸', label: '🇪🇸 +34' },
  { code: '+31', name: t('countries.nl'), flag: '🇳🇱', label: '🇳🇱 +31' },
  { code: '+32', name: t('countries.be'), flag: '🇧🇪', label: '🇧🇪 +32' },
  { code: '+46', name: t('countries.se'), flag: '🇸🇪', label: '🇸🇪 +46' },
  { code: '+1', name: t('countries.usCanada'), flag: '🇺🇸', label: '🇺🇸 +1' },
]);

const selectedCountryCode = ref<CountryCode>(countryCodes.value[0]);
const showCountryPicker = ref(false);

// Computed
const canInvite = computed(() => {
  if (inviteMethod.value === 'email') {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(inviteEmail.value.trim());
  }
  if (inviteMethod.value === 'phone') {
    const phoneDigits = invitePhone.value.replace(/\D/g, '');
    return phoneDigits.length >= 6;
  }
  return false;
});

const canConfirm = computed(() => {
  // In multiple mode, selected users can always be confirmed.
  if (props.multiple && selectedUsers.value.length > 0) {
    return true;
  }
  // For email/phone methods - check if valid input
  if (inviteMethod.value === 'email' || inviteMethod.value === 'phone') {
    return canInvite.value;
  }
  // For search method with multiple selection
  if (props.multiple) {
    return selectedUsers.value.length > 0;
  }
  return false;
});

const alreadySelectedIds = computed<Set<string>>(() => {
  const ids = (props.alreadySelected || []).map(u => String(u._id));
  const selectedIds = selectedUsers.value.map(u => String(u._id));
  return new Set([...ids, ...selectedIds]);
});

// Methods
const selectCountry = (country: CountryCode) => {
  selectedCountryCode.value = country;
  showCountryPicker.value = false;
};

const getUserLabel = (user: UserLite): string => {
  if (user.firstName && user.lastName) return `${user.firstName} ${user.lastName}`;
  if (user.name) return user.name;
  if (user.searchName) return user.searchName;
  if (user.nickName) return user.nickName;
  return user.email || 'Unknown';
};

const onUserSearch = () => {
  if (userSearchTimeout) clearTimeout(userSearchTimeout);
  
  userSearchTimeout = setTimeout(async () => {
    const q = (userSearch.value || '').trim();
    if (!q || q.length < 2) {
      userResults.value = [];
      return;
    }
    
    searchingUsers.value = true;
    try {
      // 1) Local match in my dependants/referents (always)
      const qLower = q.toLowerCase();
      const matchUser = (u: any) => {
        const haystack = [u.firstName, u.lastName, u.name, u.searchName, u.nickName, u.email]
          .filter(Boolean).join(' ').toLowerCase();
        return haystack.includes(qLower);
      };
      const localMatches: any[] = (myDependants.value || []).filter(matchUser);

      // 2) Remote search
      let rawResults: any[] = [];
      if (props.myPeopleOnly) {
        const { data } = await axiosInstance.get(`${API_BASE_URL}/users/dependants`, {
          params: { q, limit: 50 }
        });
        rawResults = Array.isArray(data?.children) ? data.children : [];
      } else {
        const { data } = await axiosInstance.get(`${API_BASE_URL}/users/search`, {
          params: { searchString: q }
        });
        rawResults = Array.isArray(data) ? data : [];
      }

      // 3) Merge: dependants first, then remote (de-duplicated)
      const seen = new Set<string>();
      const merged: any[] = [];
      for (const u of [...localMatches, ...rawResults]) {
        const id = String(u._id);
        if (seen.has(id)) continue;
        seen.add(id);
        merged.push(u);
      }

      userResults.value = merged
        .filter((user: any) => !alreadySelectedIds.value.has(String(user._id)))
        .map((user: any) => ({
          _id: user._id,
          firstName: user.firstName,
          lastName: user.lastName,
          name: user.name,
          nickName: user.nickName,
          searchName: user.searchName,
          email: user.email,
          rating: user.rating,
          avatar: getUserAvatarUrl(user) || undefined,
          _teams: user._teams || []
        }));
    } catch (error) {
      console.error('User search failed:', error);
      userResults.value = [];
    } finally {
      searchingUsers.value = false;
    }
  }, 300);
};

const openPersonDetail = (user: UserLite) => {
  playerCardRef.value?.open(user);
};

const addOrEmitUser = (user: UserLite) => {
  if (props.multiple) {
    if (!selectedUsers.value.some(u => u._id === user._id)) {
      selectedUsers.value.push(user);
    }
    addedUser.value = user;
    showUserAddedSnackbar.value = true;
    return;
  }

  emit('done', [user]);
  addedUser.value = user;
  showUserAddedSnackbar.value = true;
  handleClose();
};

const selectUserForInvite = (user: UserLite) => {
  if (props.multiple) {
    // Add to selected list
    if (!selectedUsers.value.some(u => u._id === user._id)) {
      selectedUsers.value.push(user);
    }
    // Clear search
    userSearch.value = '';
    userResults.value = [];
  } else {
    // Single selection - emit and close
    emit('done', [user]);
    emit('userSelected', user);
    handleClose();
  }
};

const removeSelectedUser = (index: number) => {
  selectedUsers.value.splice(index, 1);
};

const handleConfirm = async () => {
  // For email/phone methods with a valid value - add/find user first.
  if ((inviteMethod.value === 'email' || inviteMethod.value === 'phone') && canInvite.value) {
    await confirmInvite();
    return;
  }
  // For search method with selected users
  if (selectedUsers.value.length > 0) {
    emit('done', [...selectedUsers.value]);
    handleClose();
  }
};

const confirmInvite = async () => {
  if (inviteSubmitting.value) return;
  inviteSubmitting.value = true;
  
  try {
    if (inviteMethod.value === 'email') {
      const email = inviteEmail.value.trim();
      if (!email) return;
      
      // First try to find user by email
      try {
        const { data: user } = await axiosInstance.get(`${API_BASE_URL}/users/by-email`, { params: { email } });
        if (user?._id) {
          // User found - add them directly
          const userToAdd: UserLite = {
            _id: user._id,
            firstName: user.firstName,
            lastName: user.lastName,
            name: user.name,
            nickName: user.nickName,
            email: user.email,
            rating: user.rating,
            avatar: getUserAvatarUrl(user) || undefined
          };
          addOrEmitUser(userToAdd);
          inviteEmail.value = '';
          return;
        }
      } catch (err: any) {
        if (err?.response?.status !== 404) {
          console.error('Error finding user by email:', err);
          showNotification(t('invite.searchError'), 'error');
          return;
        }
        // 404 - User not found, show nickname dialog only where real invites are supported.
        if (!props.entityId || !props.entityType) {
          showNotification(t('noResults'), 'error');
          return;
        }
        pendingContact.value = email;
        pendingContactType.value = 'email';
        pendingNickname.value = '';
        showNicknameDialog.value = true;
        return;
      }
      
    } else if (inviteMethod.value === 'phone') {
      const phoneDigits = invitePhone.value.replace(/\D/g, '');
      if (!phoneDigits) return;
      
      const countryCodeDigits = selectedCountryCode.value.code.replace(/\D/g, '');
      const fullPhone = countryCodeDigits + phoneDigits;
      
      // First try to find user by phone
      try {
        const { data: user } = await axiosInstance.get(`${API_BASE_URL}/users/by-phone`, { params: { phone: fullPhone } });
        if (user?._id) {
          // User found - add them directly
          const userToAdd: UserLite = {
            _id: user._id,
            firstName: user.firstName,
            lastName: user.lastName,
            name: user.name,
            nickName: user.nickName,
            email: user.email,
            rating: user.rating,
            avatar: getUserAvatarUrl(user) || undefined
          };
          addOrEmitUser(userToAdd);
          invitePhone.value = '';
          return;
        }
      } catch (err: any) {
        if (err?.response?.status !== 404) {
          console.error('Error finding user by phone:', err);
          showNotification(t('invite.searchError'), 'error');
          return;
        }
        // 404 - User not found, show nickname dialog only where real invites are supported.
        if (!props.entityId || !props.entityType) {
          showNotification(t('noResults'), 'error');
          return;
        }
        pendingContact.value = '+' + fullPhone;
        pendingContactType.value = 'phone';
        pendingNickname.value = '';
        showNicknameDialog.value = true;
        return;
      }
    }
  } finally {
    inviteSubmitting.value = false;
  }
};

// Nickname dialog functions
const cancelNicknameDialog = () => {
  showNicknameDialog.value = false;
  pendingNickname.value = '';
  pendingContact.value = '';
};

const confirmNicknameAndInvite = async () => {
  if (!pendingNickname.value.trim()) return;
  if (!props.entityId || !props.entityType) {
    showNotification(t('invite.missingTeamInfo'), 'error');
    return;
  }
  
  // Validate entityId is a valid MongoDB ObjectId (24 hex chars)
  if (!/^[0-9a-fA-F]{24}$/.test(props.entityId)) {
    showNotification(t('invite.invalidTeamId'), 'error');
    return;
  }
  
  inviteSubmitting.value = true;
  showNicknameDialog.value = false;
  
  try {
    // Map frontend role keys to backend invite enum values
    const roleMap: Record<string, string> = {
      timekeepers: 'timekeeper',
      externalOrganizers: 'external-organizers',
    };
    const backendRole = roleMap[props.role || ''] || props.role || 'players';

    const payload: any = {
      entityId: props.entityId,
      entityType: props.entityType,
      role: backendRole,
      nickname: pendingNickname.value.trim(),
    };
    
    if (pendingContactType.value === 'email') {
      payload.email = pendingContact.value;
    } else {
      payload.phone = pendingContact.value.replace(/\D/g, '');
    }
    
    const { data } = await axiosInstance.post(`${API_BASE_URL}/invites`, payload);
    
    // If pending user was created, use it
    if (data.pendingUser) {
      const userToAdd: UserLite = {
        _id: data.pendingUser._id,
        firstName: data.pendingUser.firstName,
        lastName: data.pendingUser.lastName,
        name: data.pendingUser.name,
        nickName: data.pendingUser.nickName,
        email: data.pendingUser.email,
        rating: data.pendingUser.rating || 0,
        avatar: data.pendingUser.avatar || undefined
      };
      emit('done', [userToAdd]);
      addedUser.value = userToAdd;
      showUserAddedSnackbar.value = true;
    } else {
      // No pending user, just show invite sent
      inviteSentContact.value = pendingContact.value;
      showInviteSentSnackbar.value = true;
    }
    
    emit('inviteSent', { 
      email: pendingContactType.value === 'email' ? pendingContact.value : undefined,
      phone: pendingContactType.value === 'phone' ? pendingContact.value : undefined,
      entityType: props.entityType, 
      role: props.role || 'players' 
    });
    
    handleClose();
  } catch (err: any) {
    console.error('Error creating invite with pending user:', err);
    // 409 - User actually exists (race condition)
    if (err?.response?.status === 409 && err?.response?.data?.user) {
      const userData = err.response.data.user;
      const userToAdd: UserLite = {
        _id: userData._id,
        firstName: userData.firstName,
        lastName: userData.lastName,
        name: userData.name,
        nickName: userData.nickName,
        email: userData.email,
        rating: userData.rating,
        avatar: getUserAvatarUrl(userData) || undefined
      };
      emit('done', [userToAdd]);
      addedUser.value = userToAdd;
      showUserAddedSnackbar.value = true;
      handleClose();
      return;
    }
    showNotification(err?.response?.data?.message || t('invite.createUserError'), 'error');
  } finally {
    inviteSubmitting.value = false;
    pendingNickname.value = '';
    pendingContact.value = '';
  }
};

const handleClose = () => {
  // Reset state
  inviteMethod.value = 'search';
  inviteEmail.value = '';
  invitePhone.value = '';
  userSearch.value = '';
  userResults.value = [];
  selectedUsers.value = [];
  showCountryPicker.value = false;
  showNicknameDialog.value = false;
  pendingNickname.value = '';
  pendingContact.value = '';
  isOpen.value = false;
};

// Reset state when modal opens
watch(isOpen, (val) => {
  if (val) {
    inviteMethod.value = 'search';
    inviteEmail.value = '';
    invitePhone.value = '';
    userSearch.value = '';
    userResults.value = [];
    selectedUsers.value = [];
    showCountryPicker.value = false;
    showNicknameDialog.value = false;
    pendingNickname.value = '';
    pendingContact.value = '';
    fetchMyDependants();
  }
});
</script>

<style lang="scss" scoped>
// Header
.invite-header {
  position: sticky;
  top: 0;
  display: flex;
  align-items: center;
  padding: 12px 12px;
  background: rgb(var(--v-theme-sheetBackground));
  z-index: 10;
}

.header-btn {
  width: 45px;
  height: 45px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:active {
    transform: scale(0.95);
  }
  
  &--close {
    background: rgba(var(--v-theme-sheetCloseBtn), 0.65);
    backdrop-filter: blur(20px);
    -webkit-backdrop-filter: blur(20px);
    color: rgb(var(--v-theme-sheetText));
    
    &:hover {
      filter: brightness(0.92);
    }
    
    :deep(.v-icon) {
      color: rgb(var(--v-theme-sheetText)) !important;
    }
  }
  
  &--confirm {
    background: rgb(var(--v-theme-success));
    color: #ffffff;
    box-shadow: 
      0 4px 12px rgba(var(--v-theme-success), 0.4),
      inset 0 1px 0 rgba(255, 255, 255, 0.2);
    
    &:hover {
      filter: brightness(1.1);
    }
    
    :deep(.v-icon) {
      color: #ffffff !important;
    }
  }
  
  &--disabled {
    opacity: 0.5;
    pointer-events: none;
    filter: grayscale(0.5);
  }
}

.header-title {
  flex: 1;
  text-align: center;
  font-size: 1.1rem;
  font-weight: 600;
  color: rgb(var(--v-theme-sheetText));
  margin-top: 3px;
}

.invite-modal {
  padding: 0 16px 16px;
}

// Search
.search-input-wrapper-ios {
  display: flex;
  align-items: center;
  padding: 0 16px;
  min-height: 48px;
  
  .search-icon {
    color: rgba(var(--v-theme-sheetText), 0.5);
    margin-right: 12px;
  }
  
  .search-input {
    flex: 1;
    background: transparent;
    border: none;
    outline: none;
    font-size: 1rem;
    color: rgb(var(--v-theme-sheetText));
    
    &::placeholder {
      color: rgba(var(--v-theme-sheetText), 0.4);
    }
  }
}

.search-empty-inline {
  text-align: center;
  padding: 24px 16px;
  color: rgba(var(--v-theme-sheetText), 0.5);
  font-size: 0.9rem;
}

// Phone input
.phone-input-row {
  display: flex;
  align-items: center;
  padding: 8px 16px;
  gap: 8px;
}

.country-select {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 8px 12px;
  background: rgba(var(--v-theme-sheetText), 0.08);
  border-radius: 10px;
  cursor: pointer;
  transition: background 0.15s ease;
  
  &:active {
    background: rgba(var(--v-theme-sheetText), 0.15);
  }
}

.country-flag {
  font-size: 1.2rem;
}

.country-code {
  font-size: 0.9rem;
  font-weight: 500;
}

.phone-number-input {
  flex: 1;
  background: transparent;
  border: none;
  outline: none;
  font-size: 1rem;
  color: rgb(var(--v-theme-sheetText));
  padding: 8px;
  
  &::placeholder {
    color: rgba(var(--v-theme-sheetText), 0.4);
  }
}

.country-picker {
  max-height: 300px;
  overflow-y: auto;
  margin: 8px 16px;
  background: rgba(var(--v-theme-sheetText), 0.05);
  border-radius: 12px;
}

.country-option {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  gap: 12px;
  cursor: pointer;
  transition: background 0.15s ease;
  
  &:not(:last-child) {
    border-bottom: 1px solid rgba(var(--v-theme-sheetText), 0.08);
  }
  
  &:active {
    background: rgba(var(--v-theme-sheetText), 0.1);
  }
  
  .country-name {
    flex: 1;
    font-size: 0.95rem;
  }
  
  .country-code {
    color: rgba(var(--v-theme-sheetText), 0.5);
    font-size: 0.85rem;
  }
}

// Actions
.invite-actions {
  padding: 16px 0;
}

// Create dependent button
.create-dependent-wrapper {
  padding: 20px 16px 12px;
}

.create-dependent-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 13px 16px;
  font-size: 0.95rem;
  font-weight: 500;
  color: rgb(var(--v-theme-primary));
  background: rgba(var(--v-theme-primary), 0.1);
  border: none;
  border-radius: 12px;
  cursor: pointer;
  transition: background 0.15s ease;

  &:active {
    background: rgba(var(--v-theme-primary), 0.18);
  }
}
</style>

<style lang="scss">
// Snackbar global styles (not scoped)
.user-added-snackbar-content,
.invite-sent-snackbar-content {
  background: rgba(40, 40, 40, 0.85) !important;
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border-radius: 16px !important;
  padding: 12px 16px !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
}
</style>

<style lang="scss" scoped>
// Rich notifications
.user-added-notification,
.invite-sent-notification {
  display: flex;
  align-items: center;
  gap: 12px;
}

.user-added-avatar {
  position: relative;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
  border: 2px solid transparent;
  
  &.ring-gray { border-color: #9e9e9e; }
  &.ring-green { border-color: #4caf50; }
  &.ring-blue { border-color: #2196f3; }
  &.ring-purple { border-color: #9c27b0; }
  &.ring-gold { border-color: #ffc107; }
  &.ring-red { border-color: #f44336; }
  
  .user-added-avatar-img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  
  .user-added-initials {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(255, 255, 255, 0.2);
    font-weight: 600;
    font-size: 14px;
    color: white;
  }
}

.user-added-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  
  .user-added-name {
    font-weight: 600;
    font-size: 14px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  
  .user-added-subtitle {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    opacity: 0.7;
    
    .user-added-rating {
      font-weight: 500;
    }
    
    .user-added-status {
      color: rgb(var(--v-theme-success));
    }
  }
}

.invite-sent-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  
  .invite-sent-title {
    font-weight: 600;
    font-size: 14px;
  }
  
  .invite-sent-contact {
    font-size: 12px;
    opacity: 0.7;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

// Nickname dialog
.nickname-dialog {
  text-align: center;
  
  .nickname-dialog-icon {
    margin-bottom: 16px;
  }
  
  .nickname-dialog-title {
    font-size: 1.25rem;
    font-weight: 600;
    margin-bottom: 8px;
    color: rgb(var(--v-theme-sheetText));
  }
  
  .nickname-dialog-subtitle {
    font-size: 0.9rem;
    color: rgba(var(--v-theme-sheetText), 0.7);
    margin-bottom: 24px;
    line-height: 1.5;
    
    strong {
      color: rgb(var(--v-theme-sheetText));
    }
  }
  
  .nickname-input {
    margin-bottom: 24px;
  }
  
  .nickname-dialog-actions {
    display: flex;
    gap: 12px;
    justify-content: center;
  }
  
  .nickname-btn {
    padding: 12px 24px;
    border-radius: 12px;
    font-size: 0.95rem;
    font-weight: 600;
    border: none;
    cursor: pointer;
    transition: all 0.2s ease;
    
    &--cancel {
      background: rgba(var(--v-theme-sheetText), 0.1);
      color: rgb(var(--v-theme-sheetText));
      
      &:hover {
        background: rgba(var(--v-theme-sheetText), 0.15);
      }
    }
    
    &--confirm {
      background: rgb(var(--v-theme-primary));
      color: white;
      
      &:hover:not(:disabled) {
        filter: brightness(1.1);
      }
      
      &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }
    }
  }
}
</style>
